import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Footer } from '@/components/Footer';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { ClinicTextInput } from '@/components/ClinicTextInput';
import { ClinicDobInput } from '@/components/ClinicDobInput';
import { BookingApi } from '@/utils/api';
import { PatientNotFoundModal } from '@/components/PatientNotFoundModal';

type DateOfBirth = {
  month: string;
  day: string;
  year: string;
};

type BasicInfo = {
  firstName: string;
  lastName: string;
  dateOfBirth: DateOfBirth;
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

export function BasicInfoPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError, config, configLoading } = useClinicConfigContext();

  const [form, setForm] = useState<BasicInfo>({
    firstName: '',
    lastName: '',
    dateOfBirth: { month: '', day: '', year: '' },
  });

  const [errors, setErrors] = useState<Partial<BasicInfo>>({});
  const [isReturningPatient, setIsReturningPatient] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [showPatientNotFoundModal, setShowPatientNotFoundModal] = useState<boolean>(false);

  useEffect(() => {
    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(
        new RegExp(
          '(?:^|; )' +
            name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') +
            '=([^;]*)'
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    };
    const explicit = getCookie('is_returning_patient');
    const typeCookie = getCookie('patient_type');
    if (explicit !== null) {
      setIsReturningPatient(explicit === 'true');
    } else if (typeCookie) {
      setIsReturningPatient(typeCookie === 'returning');
    }
  }, []);

  const updateTextField = (key: 'firstName' | 'lastName', value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const updateDob = (key: keyof DateOfBirth, value: string) => {
    setForm(prev => ({
      ...prev,
      dateOfBirth: { ...prev.dateOfBirth, [key]: value },
    }));
    if (errors.dateOfBirth)
      setErrors(prev => ({ ...prev, dateOfBirth: undefined }));
  };

  const isValid = () => {
    const newErrors: Partial<BasicInfo> = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (
      !form.dateOfBirth.month ||
      !form.dateOfBirth.day ||
      !form.dateOfBirth.year
    ) {
      newErrors.dateOfBirth = {
        month: 'Date of birth is required',
        day: '',
        year: '',
      };
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!clinicSlug) return;
    if (!isValid()) return;

    const dob = `${form.dateOfBirth.year}-${form.dateOfBirth.month}-${form.dateOfBirth.day}`;

    // Save basic info to cookies immediately
    document.cookie = `patient_first_name=${encodeURIComponent(form.firstName)}; path=/; max-age=604800`;
    document.cookie = `patient_last_name=${encodeURIComponent(form.lastName)}; path=/; max-age=604800`;
    document.cookie = `patient_dob=${encodeURIComponent(dob)}; path=/; max-age=604800`;

    // Attempt returning-patient verification with minimal payload
    try {
      setIsVerifying(true);
      const result = await BookingApi.verifyReturningPatient(clinicSlug, {
        first_name: form.firstName,
        last_name: form.lastName,
        DoB: dob,
      });

      if (result?.data?.data?.patient_verified) {
        // Persist returned patient info
        const info = result.data.data.patient_info;
        if (info) {
          document.cookie = `existing_patient_id=${encodeURIComponent(String(info.id))}; path=/; max-age=604800`;
          document.cookie = `patient_phone_number=${encodeURIComponent(info.phone || '')}; path=/; max-age=604800`;
          document.cookie = `patient_email=${encodeURIComponent(info.email || '')}; path=/; max-age=604800`;
        }

        // Navigate based on show_patient_review setting
        const shouldShowTestimonial = config?.booking_settings?.show_patient_review ?? true;
        if (shouldShowTestimonial) {
          navigate(`/${clinicSlug}/testimonial`);
        } else {
          navigate(`/${clinicSlug}/patient-service`);
        }
        return;
      }
      
      // Patient not found - show modal
      setShowPatientNotFoundModal(true);
    } catch (e) {
      console.error('verify-returning failed:', e);
      // On error, also show the modal to allow continuing
      setShowPatientNotFoundModal(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleContinueFromModal = () => {
    if (!clinicSlug) return;
    setShowPatientNotFoundModal(false);
    
    // Navigate based on show_patient_review setting
    const shouldShowTestimonial = config?.booking_settings?.show_patient_review ?? true;
    if (shouldShowTestimonial) {
      navigate(`/${clinicSlug}/testimonial`);
    } else {
      navigate(`/${clinicSlug}/patient-service`);
    }
  };

  if (clinicLoading || configLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
    >
      <motion.div className='flex-shrink-0 p-6' variants={fadeInUp}>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </motion.div>

      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-3xl mx-auto w-full'>
          <motion.div className='text-left mb-12 mt-8' variants={fadeInUp}>
            <h2
              className='text-2xl lg:text-6xl font-semibold'
              style={{ color: 'var(--clinic-text)' }}
            >
              {isReturningPatient
                ? 'Great, please provide details below, so I can pull up your file'
                : 'Nice to meet you! Please provide your information.'}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            <motion.div className='space-y-6' variants={fadeInUp}>
              <motion.div variants={fadeInUp}>
                <ClinicTextInput
                  label='First Name'
                  value={form.firstName}
                  onChange={v => updateTextField('firstName', v)}
                  placeholder='your first name'
                  error={errors.firstName as string}
                  className='placeholder-clinic-text'
                />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <ClinicTextInput
                  label='Last Name'
                  value={form.lastName}
                  onChange={v => updateTextField('lastName', v)}
                  placeholder='your last name'
                  error={errors.lastName as string}
                  className='placeholder-clinic-text'
                />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <ClinicDobInput
                  value={form.dateOfBirth}
                  onChange={(f, v) => updateDob(f, v)}
                  error={
                    errors.dateOfBirth ? 'Date of birth is required' : undefined
                  }
                  className='placeholder-clinic-text'
                />
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div className='flex justify-start mt-12' variants={scaleIn}>
            <motion.button
              disabled={isVerifying}
              onClick={handleNext}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5  font-semibold hover:opacity-90 ${
                isVerifying ? 'opacity-80 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: 'var(--clinic-secondary)',
                color: 'var(--clinic-text)',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isVerifying ? (
                <>
                  <LoadingSpinner size='sm' text='' />
                  Verifying...
                </>
              ) : (
                'Next'
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>

      <motion.div variants={fadeInUp}>
        <Footer mobileColumnLayout={true} />
      </motion.div>

      {/* Patient Not Found Modal */}
      <PatientNotFoundModal
        isOpen={showPatientNotFoundModal}
        onContinue={handleContinueFromModal}
      />
    </motion.div>
  );
}

export default BasicInfoPage;
